import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Card, Row, Col, Space } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { fetchPartners } from '../services/api';
import { fetchSchemas } from '../services/schemaApi';

const { Option } = Select;
const { TextArea } = Input;

export default function MethodForm({ handleSubmitMethod, editingMethod = null }) {
    const [form] = Form.useForm();
    const [partners, setPartners] = useState([]);
    const [schemas, setSchemas] = useState([]);

    useEffect(() => {
        fetchPartners().then(res => setPartners(res.data || []));
        fetchSchemas().then(res => setSchemas(res.data || []));
    }, []);

    useEffect(() => {
        if (editingMethod) {
            const { requestHeaders = {}, requestParams = {}, ...rest } = editingMethod;
            form.setFieldsValue({
                ...rest,
                requestHeaders: Object.entries(requestHeaders).map(([key, value]) => ({ key, value })),
                requestParams: Object.entries(requestParams).map(([key, value]) => ({ key, value })),
            });
        }
    }, [editingMethod, form]);

    const onFinish = (values) => {
        const requestHeaders = {};
        const requestParams = {};
        values.requestHeaders?.forEach(h => { requestHeaders[h.key] = h.value });
        values.requestParams?.forEach(p => { requestParams[p.key] = p.value });

        const finalData = {
            ...values,
            requestHeaders,
            requestParams,
        };

        if (editingMethod?.id) finalData.id = editingMethod.id;
        handleSubmitMethod(finalData);
        form.resetFields();
    };

    const eventType = Form.useWatch('eventType', form);

    const getTargetUrlLabel = () => {
        if (eventType === 'kafka') return 'Topic';
        if (eventType === 'queue') return 'Queue Name';
        return 'Target URL';
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ eventType: 'api', contentType: 'application/json', direction: 'pull' }}
        >
            <Card title="Thông tin phương thức">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="partnerId" label="Đối tác" rules={[{ required: true }]}>
                            <Select placeholder="Chọn đối tác">
                                {partners.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="methodName" label="Tên phương thức" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="eventType" label="Loại sự kiện">
                            <Select>
                                <Option value="api">API</Option>
                                <Option value="kafka">Kafka</Option>
                                <Option value="queue">Queue</Option>
                                <Option value="sql">SQL</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="direction" label="Chiều dữ liệu">
                            <Select>
                                <Option value="pull">Pull</Option>
                                <Option value="push">Push</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {eventType === 'api' && (
                        <>
                            <Col span={12}>
                                <Form.Item name="httpMethod" label="HTTP Method">
                                    <Select>
                                        <Option value="GET">GET</Option>
                                        <Option value="POST">POST</Option>
                                        <Option value="PUT">PUT</Option>
                                        <Option value="DELETE">DELETE</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="contentType" label="Content-Type">
                                    <Input placeholder="application/json" />
                                </Form.Item>
                            </Col>
                        </>
                    )}

                    <Col span={12}>
                        {eventType !== 'sql' ? (
                            <Form.Item name="targetUrl" label={getTargetUrlLabel()}>
                                <Input />
                            </Form.Item>
                        ) :""}
                    </Col>

                    <Col span={12}>
                        <Form.Item name="schemaId" label="Schema Mapping">
                            <Select placeholder="Chọn schema">
                                {schemas.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            {eventType === 'api' && (
                <>
                    <Card title="Request Headers" style={{ marginTop: 16 }}>
                        <Form.List name="requestHeaders">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item {...restField} name={[name, 'key']} rules={[{ required: true }]}>
                                                <Input placeholder="Header Key" />
                                            </Form.Item>
                                            <Form.Item {...restField} name={[name, 'value']} rules={[{ required: true }]}>
                                                <Input placeholder="Header Value" />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Thêm Header
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Card>

                    <Card title="Request Params" style={{ marginTop: 16 }}>
                        <Form.List name="requestParams">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item {...restField} name={[name, 'key']} rules={[{ required: true }]}>
                                                <Input placeholder="Param Key" />
                                            </Form.Item>
                                            <Form.Item {...restField} name={[name, 'value']} rules={[{ required: true }]}>
                                                <Input placeholder="Param Value" />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Thêm Param
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Card>
                </>
            )}

            <div style={{ textAlign: 'right', marginTop: 24 }}>
                <Button type="primary" htmlType="submit">Lưu phương thức</Button>
            </div>
        </Form>
    );
}
